import { useState } from 'react';
import { ChevronUp, ChevronDown, Eraser } from 'lucide-react';
import { AudioPlayer } from './AudioPlayer';
import { GuildThemePicker } from './GuildThemePicker';

interface FooterProps {
    onResetSession?: () => void;
    deckColors?: string[];
    channelName?: string;
}

export function Footer({ onResetSession, deckColors = [], channelName }: FooterProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <footer className="fixed bottom-0 left-0 right-0 z-40">
            {/* Toggle button - left aligned with logo */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="absolute left-4 -top-8 px-2 py-1 bg-[#0a0a1a] border border-purple-500/20 border-b-0 rounded-t-lg hover:bg-purple-500/10 transition-colors flex items-center gap-1"
                title={isExpanded ? 'Hide footer' : 'Show DECKSTR info'}
            >
                <img
                    src="/jud-mascot.png"
                    alt="DECKSTR"
                    className="h-5"
                />
                {isExpanded ? <ChevronDown className="size-3 text-white/40" /> : <ChevronUp className="size-3 text-white/40" />}
            </button>

            {/* Collapsible footer content */}
            <div
                className={`bg-gradient-to-r from-[#050510] via-[#0a0a1a] to-[#050510] border-t border-purple-500/20 transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-40 py-3 sm:py-4' : 'max-h-0 py-0'
                    }`}
            >
                <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 px-3 sm:px-4">
                    {/* Discord Link */}
                    <a
                        href="https://discord.gg/2JARe8yQx7"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5 px-3 py-1.5 bg-[#5865F2]/10 hover:bg-[#5865F2]/20 border border-[#5865F2]/20 hover:border-[#5865F2]/40 rounded-lg transition-all group"
                        title="Join the DECKSTR Discord community"
                    >
                        {/* Official Discord Icon */}
                        <svg className="size-5 text-[#5865F2] group-hover:text-[#7289da] transition-colors" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9460 2.4189-2.1568 2.4189z" />
                        </svg>
                        <div className="flex flex-col">
                            <span className="text-white/70 text-xs font-medium group-hover:text-[#5865F2] transition-colors">
                                Discord
                            </span>
                            <span className="text-white/30 text-[10px]">
                                Join our community
                            </span>
                        </div>
                    </a>

                    {/* Divider */}
                    <div className="w-px h-8 bg-white/10 hidden sm:block" />

                    {/* Ko-fi Support Link */}
                    <a
                        href="https://ko-fi.com/deckstrgg"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 hover:border-purple-500/40 rounded-lg transition-all group"
                        title="Support DECKSTR.GG on Ko-fi"
                    >
                        <span className="text-lg">☕</span>
                        <div className="flex flex-col">
                            <span className="text-white/70 text-xs font-medium group-hover:text-purple-300 transition-colors">
                                Support Us
                            </span>
                            <span className="text-white/30 text-[10px]">
                                Ko-fi
                            </span>
                        </div>
                    </a>

                    {/* Divider */}
                    <div className="w-px h-8 bg-white/10 hidden sm:block" />

                    {/* Guild Color Theme Picker */}
                    <GuildThemePicker />

                    {/* Divider */}
                    <div className="w-px h-8 bg-white/10 hidden sm:block" />

                    {/* Credits/Links */}
                    <div className="hidden sm:flex flex-col text-xs text-white/30 gap-1">
                        <span>MTG Strategy & Analytics</span>
                        <span className="text-purple-400/50">v2.0 • Built with ♥</span>
                    </div>

                    {/* Divider */}
                    <div className="w-px h-8 bg-white/10" />

                    {/* Audio Player */}
                    <div className="flex items-center">
                        <AudioPlayer defaultVolume={50} deckColors={deckColors} channelName={channelName} />
                    </div>

                    {/* Reset Session Button */}
                    {onResetSession && (
                        <>
                            <div className="w-px h-8 bg-white/10" />
                            <button
                                type="button"
                                onClick={onResetSession}
                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-red-500/20 text-white/40 hover:text-red-400 rounded-lg transition-colors"
                                title="Reset session - Clear cache and start fresh"
                            >
                                <Eraser className="size-4" />
                                <span className="text-xs">Clear Cache</span>
                            </button>
                        </>
                    )}
                </div>
            </div>
        </footer>
    );
}
